import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingBag, Zap, Shield, Smartphone, Laptop, Globe, ArrowRight, Filter, Search, Grid, List as ListIcon } from 'lucide-react';
import SEO from '../components/SEO';
import { getProducts, getCategories } from '../services/productService';
import { API_BASE_URL } from '../services/api';
import { Link } from 'react-router-dom';

const Products = () => {
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeCategory, setActiveCategory] = useState('All');
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [prodRes, catRes] = await Promise.all([getProducts(), getCategories()]);
            setProducts(prodRes.data || []);
            setCategories(catRes.data || []);
        } catch (error) {
            console.error("Error fetching products:", error);
        } finally {
            setLoading(false);
        }
    };

    const filteredProducts = products.filter(product => {
        const matchesCategory = activeCategory === 'All' || product.category_id?.name === activeCategory;
        const matchesSearch = product.product_title.toLowerCase().includes(searchQuery.toLowerCase()) ||
            product.short_description?.toLowerCase().includes(searchQuery.toLowerCase());
        return matchesCategory && matchesSearch;
    });

    return (
        <div className="min-h-screen bg-white dark:bg-[#030014] pt-20 md:pt-32 pb-10 md:pb-20">
            <SEO
                title="Innovative Digital Products"
                description="Explore MehareTech's suite of high-performance products designed for modern businesses."
            />

            <div className="container mx-auto px-6">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center max-w-3xl mx-auto mb-6 md:mb-16"
                >
                    <h1 className="text-2xl md:text-5xl font-black text-slate-900 dark:text-white mb-2 md:mb-6">
                        Innovative <span className="text-gradient">Products</span>
                    </h1>
                    <p className="text-xs md:text-lg text-slate-600 dark:text-slate-400 leading-relaxed max-w-xl mx-auto">
                        Discover top-tier digital solutions, from AI-powered CRMs to secure payment gateways, all crafted with precision.
                    </p>
                </motion.div>

                {/* Filters & Search */}
                <div className="flex flex-col lg:flex-row gap-3 md:gap-6 mb-6 md:mb-12 items-center justify-between">
                    <div className="flex flex-wrap gap-1.5 justify-center lg:justify-start">
                        <button
                            onClick={() => setActiveCategory('All')}
                            className={`px-3 md:px-6 py-1.5 md:py-2.5 rounded-lg md:rounded-2xl text-[8px] md:text-[10px] font-black uppercase tracking-widest transition-all border ${activeCategory === 'All'
                                ? 'bg-primary border-primary text-white shadow-xl shadow-primary/20'
                                : 'bg-slate-50 dark:bg-white/5 border-slate-200 dark:border-white/10 text-slate-600 dark:text-slate-400 hover:border-primary/50'
                                }`}
                        >
                            All
                        </button>
                        {categories.map((cat) => (
                            <button
                                key={cat._id}
                                onClick={() => setActiveCategory(cat.name)}
                                className={`px-3 md:px-6 py-1.5 md:py-2.5 rounded-lg md:rounded-2xl text-[8px] md:text-[10px] font-black uppercase tracking-widest transition-all border ${activeCategory === cat.name
                                    ? 'bg-primary border-primary text-white shadow-xl shadow-primary/20'
                                    : 'bg-slate-50 dark:bg-white/5 border-slate-200 dark:border-white/10 text-slate-600 dark:text-slate-400 hover:border-primary/50'
                                    }`}
                            >
                                {cat.name}
                            </button>
                        ))}
                    </div>

                    <div className="relative w-full lg:w-80 group">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-primary transition-colors" size={14} />
                        <input
                            type="text"
                            placeholder="Search projects..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-lg md:rounded-[2rem] py-2 md:py-3.5 pl-11 pr-4 text-slate-900 dark:text-white focus:outline-none focus:border-primary/50 transition-all text-[10px] md:text-sm font-medium"
                        />
                    </div>
                </div>

                {loading ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                        {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
                            <div key={i} className="h-[400px] bg-slate-100 dark:bg-white/5 rounded-[2.5rem] animate-pulse border border-slate-200 dark:border-white/10"></div>
                        ))}
                    </div>
                ) : (
                    <>
                        <motion.div
                            layout
                            className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6"
                        >
                            <AnimatePresence mode='popLayout'>
                                {filteredProducts.map((product) => (
                                    <motion.div
                                        key={product._id}
                                        layout
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, scale: 0.95 }}
                                        whileHover={{ y: -6 }}
                                        className="relative p-4 md:p-6 bg-slate-50 dark:bg-white/5 rounded-[1.5rem] md:rounded-[2.5rem] border border-slate-100 dark:border-white/10 group hover:border-primary/50 transition-all shadow-md hover:shadow-lg dark:shadow-none overflow-hidden flex flex-col h-full"
                                    >
                                        <div className="relative h-36 md:h-44 -mx-4 md:-mx-6 -mt-4 md:-mt-6 mb-4 md:mb-6 overflow-hidden">
                                            <img
                                                src={product.thumbnail_image ? `${API_BASE_URL}${product.thumbnail_image}` : 'https://images.unsplash.com/photo-1550745165-9bc0b252726f?auto=format&fit=crop&q=80&w=600'}
                                                alt={product.product_title}
                                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                                            />
                                            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent"></div>
                                            <div className="absolute top-3 left-3 md:top-4 md:left-4">
                                                <span className="px-2 py-0.5 md:px-3 md:py-1 bg-primary backdrop-blur-md text-[8px] md:text-[9px] font-black uppercase tracking-widest text-white rounded-full">
                                                    {product.category_id?.name || 'Digital'}
                                                </span>
                                            </div>
                                        </div>

                                        <div className="flex-grow">
                                            <h3 className="text-base md:text-lg font-black text-slate-900 dark:text-white mb-1 md:mb-2 group-hover:text-primary transition-colors line-clamp-1">
                                                {product.product_title}
                                            </h3>
                                            <p className="text-slate-600 dark:text-slate-400 mb-3 md:mb-4 text-[10px] md:text-xs leading-relaxed line-clamp-2">
                                                {product.short_description}
                                            </p>

                                            <div className="flex flex-wrap gap-1.5 mb-6">
                                                {(product.features || []).slice(0, 2).map((feature, fidx) => (
                                                    <span key={fidx} className="px-2 py-0.5 bg-white dark:bg-white/10 text-[8px] font-black uppercase tracking-widest text-primary border border-primary/20 rounded-full">
                                                        {feature}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="mt-auto pt-3 md:pt-4 border-t border-slate-200 dark:border-white/10 flex items-center justify-between">
                                            <div className="flex flex-col">
                                                <span className="text-[7px] md:text-[8px] text-slate-400 uppercase font-black tracking-widest">Pricing</span>
                                                <span className="text-base md:text-lg font-black text-slate-900 dark:text-white">
                                                    ₹{product.sale_price || product.regular_price}
                                                </span>
                                            </div>
                                            <Link
                                                to={`/products/${product.slug}`}
                                                className="inline-flex items-center gap-2 text-primary font-black uppercase tracking-widest text-[10px] hover:gap-3 transition-all"
                                            >
                                                Details <ArrowRight size={14} />
                                            </Link>
                                        </div>
                                    </motion.div>
                                ))}
                            </AnimatePresence>
                        </motion.div>

                        {filteredProducts.length === 0 && (
                            <div className="text-center py-32 bg-slate-50 dark:bg-white/5 rounded-[4rem] border border-dashed border-slate-200 dark:border-white/10">
                                <ShoppingBag size={48} className="mx-auto text-slate-200 dark:text-white/10 mb-6" />
                                <h2 className="text-2xl font-black text-slate-900 dark:text-white mb-2">No products found</h2>
                                <p className="text-slate-500 dark:text-slate-400">Try adjusting your filters or search query.</p>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default Products;
